import React, { useState, useRef, useEffect } from 'react';

export function EditableCell({ value, onSave, field, projectId, multiline = false, readOnly = false, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editing]);

  const save = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== (value || '').trim()) {
      onSave(trimmed);
    }
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value || '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      cancel();
    }
  };

  if (readOnly) {
    return (
      <span
        data-testid={`project-table-cell-${field}-${projectId}`}
        className={`block ${className}`}
      >
        {value || '—'}
      </span>
    );
  }

  if (editing) {
    const Tag = multiline ? 'textarea' : 'input';
    return (
      <Tag
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        data-testid={`project-table-cell-${field}-${projectId}`}
        className={`w-full rounded-md border border-input bg-background px-2 py-1 text-sm 
          focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] focus:ring-offset-1
          ${multiline ? 'min-h-[60px] resize-y' : ''}
          ${className}`}
        rows={multiline ? 2 : undefined}
      />
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => e.key === 'Enter' && setEditing(true)}
      data-testid={`project-table-cell-${field}-${projectId}`}
      className={`group relative cursor-text rounded px-2 py-1 text-sm hover:bg-muted/60 transition-colors min-h-[28px] ${className}`}
    >
      {value || <span className="text-muted-foreground italic">Click to edit</span>}
    </div>
  );
}
